import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  UserCog,
  Plus,
  Trash2,
  Shield,
  User,
  Crown,
  Loader2,
  CheckCircle,
  AlertCircle,
  Mail,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = {
  owner: { label: 'Dueño', icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-500/20', description: 'Control total de la clínica' },
  admin: { label: 'Administrador', icon: Shield, color: 'text-blue-400', bg: 'bg-blue-500/20', description: 'Gestión completa excepto facturación' },
  staff: { label: 'Personal', icon: User, color: 'text-green-400', bg: 'bg-green-500/20', description: 'Acceso a leads, citas y conversaciones' }
};

export default function Team() {
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'staff'
  });

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/clinic/${clinicId}/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members || []);
        setCurrentUserRole(data.current_user_role);
      }
    } catch (err) {
      console.error('Error loading team:', err);
    }
    setLoading(false);
  };

  const inviteMember = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      setMessage({ type: 'error', text: 'Completa todos los campos' });
      return;
    }

    setInviting(true);
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/clinic/${clinicId}/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(inviteForm)
      });
      
      if (res.ok) {
        setMessage({ type: 'success', text: '¡Invitación enviada!' });
        setShowInvite(false);
        setInviteForm({ email: '', name: '', role: 'staff' });
        loadTeam();
      } else {
        const error = await res.json();
        setMessage({ type: 'error', text: error.detail || 'Error al invitar' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error al enviar invitación' });
    }
    setInviting(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const removeMember = async (memberId) => {
    if (!confirm('¿Estás seguro de eliminar este miembro?')) return;
    
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/clinic/${clinicId}/team/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setMessage({ type: 'success', text: 'Miembro eliminado' });
        loadTeam();
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error al eliminar' });
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const updateRole = async (memberId, newRole) => {
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/clinic/${clinicId}/team/${memberId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });
      
      if (res.ok) {
        setMessage({ type: 'success', text: 'Rol actualizado' });
        loadTeam();
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error al actualizar rol' });
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const canManageTeam = currentUserRole === 'owner' || currentUserRole === 'admin';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <UserCog className="w-7 h-7 text-blue-500" />
              Equipo
            </h1>
            <p className="text-gray-500 mt-1">
              Gestiona los usuarios de tu clínica
            </p>
          </div>
          {canManageTeam && (
            <Button
              onClick={() => setShowInvite(true)}
              className="bg-gradient-to-r from-blue-600 to-cyan-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Invitar Usuario
            </Button>
          )}
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {/* Role Legend */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(ROLES).map(([key, role]) => {
            const Icon = role.icon;
            return (
              <Card key={key} className="bg-slate-800/30 border-slate-700">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${role.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${role.color}`} />
                  </div>
                  <div>
                    <p className="font-medium text-white">{role.label}</p>
                    <p className="text-xs text-gray-500">{role.description}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Team Members */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg">Miembros del Equipo</CardTitle>
            <CardDescription>{members.length} usuario(s) en tu clínica</CardDescription>
          </CardHeader>
          <CardContent>
            {members.length > 0 ? (
              <div className="space-y-3">
                {members.map((member) => {
                  const roleConfig = ROLES[member.role] || ROLES.staff;
                  const RoleIcon = roleConfig.icon;
                  const isCurrentUser = member.is_current_user;
                  
                  return (
                    <div
                      key={member.id}
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        isCurrentUser ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-slate-700/30'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full ${roleConfig.bg} flex items-center justify-center`}>
                          <span className="text-lg font-bold text-white">
                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white">{member.name}</p>
                            {isCurrentUser && (
                              <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">
                                Tú
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{member.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {/* Role Badge/Selector */}
                        {canManageTeam && !isCurrentUser && member.role !== 'owner' ? (
                          <select
                            value={member.role}
                            onChange={(e) => updateRole(member.id, e.target.value)}
                            className="bg-slate-700 border-slate-600 text-white text-sm rounded-lg px-3 py-2"
                          >
                            <option value="admin">Administrador</option>
                            <option value="staff">Personal</option>
                          </select>
                        ) : (
                          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${roleConfig.bg}`}>
                            <RoleIcon className={`w-4 h-4 ${roleConfig.color}`} />
                            <span className={`text-sm font-medium ${roleConfig.color}`}>
                              {roleConfig.label}
                            </span>
                          </div>
                        )}
                        
                        {/* Delete Button */}
                        {canManageTeam && !isCurrentUser && member.role !== 'owner' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMember(member.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No hay miembros en el equipo
              </p>
            )}
          </CardContent>
        </Card>

        {/* Invite Modal */}
        {showInvite && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
            <Card className="w-full max-w-md bg-slate-800 border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-blue-400" />
                    Invitar Usuario
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowInvite(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <CardDescription>
                  El usuario recibirá un email con instrucciones para unirse
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Nombre</label>
                  <Input
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    placeholder="Juan Pérez"
                    className="bg-slate-700/50 border-slate-600"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Email</label>
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="juan@ejemplo.com"
                    className="bg-slate-700/50 border-slate-600"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Rol</label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className="w-full bg-slate-700/50 border border-slate-600 text-white rounded-lg px-3 py-2"
                  >
                    <option value="admin">Administrador</option>
                    <option value="staff">Personal</option>
                  </select>
                </div>
                <Button
                  onClick={inviteMember}
                  disabled={inviting}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600"
                >
                  {inviting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Enviar Invitación
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
